import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Activity, BarChart3, DollarSign, Shield, TrendingUp, Zap } from "lucide-react";
import { useEOSAuth } from "../../contexts/EOSAuthContext";

export type BrokerOption = {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  iconColor: string;
  color: string;
  popular?: boolean;
};

export type Holding = {
  symbol?: string;
  last_price?: number;
  quantity?: number;
  [key: string]: unknown;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://engineeringofstockbackend.onrender.com";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

export function useBrokerSelection() {
  const { user, updateBroker, token } = useEOSAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  }, [token]);

  const [selectedBroker, setSelectedBroker] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string | null>(null);
  const [portfolioData, setPortfolioData] = useState<Holding[] | null>(null);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const [accountBalance, setAccountBalance] = useState<number | null>(null);
  const [showBrokerSelection, setShowBrokerSelection] = useState(false);

  const checkZerodhaStatus = useCallback(async () => {
    try {
      const response = await api.get("/api/auth/zerodha/status");
      const data = response.data;

      if (data.authenticated && user?.broker === "connecting") {
        await updateBroker("zerodha");
        setConnectionStatus("connected");
      } else {
        setConnectionStatus(data.authenticated ? "connected" : "disconnected");
      }
    } catch {
      setConnectionStatus("error");
    }
  }, [user?.broker, updateBroker]);

  const fetchPortfolioData = useCallback(async () => {
    if (user?.broker !== "zerodha") return;

    setLoadingPortfolio(true);
    try {
      const response = await api.get("/api/trading/holdings");
      if (response.data.success) {
        setPortfolioData(response.data?.data || []);
      }
    } catch {
      // noop
    } finally {
      setLoadingPortfolio(false);
    }
  }, [user?.broker]);

  const fetchAccountBalance = useCallback(async () => {
    if (user?.broker !== "zerodha") return;

    try {
      const response = await api.get("/api/trading/account-balance");
      if (response.data.success) {
        setAccountBalance(response.data?.data?.balance ?? null);
      }
    } catch {
      // noop
    }
  }, [user?.broker]);

  useEffect(() => {
    if (user?.broker === "connecting") {
      void checkZerodhaStatus();
      const interval = setInterval(() => {
        void checkZerodhaStatus();
      }, 5000);
      return () => clearInterval(interval);
    }

    if (user?.broker === "zerodha") {
      void checkZerodhaStatus();
    }

    return undefined;
  }, [user?.broker, checkZerodhaStatus]);

  useEffect(() => {
    if (user?.broker === "zerodha" && connectionStatus === "connected" && !showBrokerSelection) {
      void fetchPortfolioData();
      void fetchAccountBalance();
    }
  }, [user?.broker, connectionStatus, showBrokerSelection, fetchPortfolioData, fetchAccountBalance]);

  const handleChangeBroker = async () => {
    try {
      if (user?.broker === "zerodha") {
        await api.post("/api/auth/zerodha/logout");
      }

      await updateBroker("none");
      setShowBrokerSelection(true);
      setConnectionStatus(null);
      setPortfolioData(null);
      setAccountBalance(null);
    } catch {
      // noop
    }
  };

  const brokers: BrokerOption[] = useMemo(
    () => [
      {
        id: "zerodha",
        name: "Zerodha",
        description: "India's largest stock broker by active retail clients",
        icon: Zap,
        iconColor: "text-emerald-600",
        color: "from-emerald-500 to-emerald-600",
        popular: true,
      },
      {
        id: "upstox",
        name: "Upstox",
        description: "Discount broker with advanced trading tools",
        icon: TrendingUp,
        iconColor: "text-teal-600",
        color: "from-teal-500 to-teal-600",
      },
      {
        id: "angelone",
        name: "Angel One",
        description: "Full-service broker with research and advisory",
        icon: Activity,
        iconColor: "text-green-600",
        color: "from-green-500 to-green-600",
      },
      {
        id: "icicidirect",
        name: "ICICI Direct",
        description: "Bank-backed broker with comprehensive services",
        icon: Shield,
        iconColor: "text-emerald-600",
        color: "from-emerald-500 to-emerald-600",
      },
      {
        id: "kotak",
        name: "Kotak Securities",
        description: "Premium broker with wealth management",
        icon: DollarSign,
        iconColor: "text-green-600",
        color: "from-green-500 to-green-600",
      },
      {
        id: "hdfc",
        name: "HDFC Securities",
        description: "Bank-backed broker with research focus",
        icon: BarChart3,
        iconColor: "text-teal-600",
        color: "from-green-500 to-green-600",
      },
    ],
    [],
  );

  const handleBrokerSelect = async () => {
    if (!selectedBroker) return;

    setIsUpdating(true);
    try {
      if (selectedBroker === "zerodha") {
        const response = await api.get("/api/auth/zerodha/login-url");
        const data = response.data;
        if (data.url) {
          window.open(data.url, "_blank", "width=600,height=700");
          await updateBroker("connecting");
          alert("Zerodha login window opened. Please complete the authentication and return here.");
          navigate("/dashboard");
        } else {
          throw new Error("Failed to get Zerodha login URL");
        }
      } else {
        await updateBroker(selectedBroker);
        navigate("/dashboard");
      }
    } catch {
      alert("Failed to connect broker. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const isConnected = user?.broker === "zerodha" && connectionStatus === "connected" && !showBrokerSelection;
  const isConnecting = user?.broker === "connecting";
  const shouldShowBrokerSelection = user?.broker === "none" || showBrokerSelection || (!isConnected && !isConnecting);

  return {
    selectedBroker,
    setSelectedBroker,
    isUpdating,
    portfolioData,
    loadingPortfolio,
    accountBalance,
    brokers,
    handleChangeBroker,
    handleBrokerSelect,
    fetchAccountBalance,
    fetchPortfolioData,
    isConnected,
    shouldShowBrokerSelection,
  };
}
